import { createVNode, render, nextTick, type ComponentPublicInstance } from 'vue'
type VueInstance = ComponentPublicInstance
import { ToTooltip, $tip } from '@touchvue/components'
import { hasClass } from './dom'
import { t } from '@touchvue/locale'

interface ValidationRule {
	rule: RegExp
	desc: string
	remark?: string
}

interface Regs {
	[key: string]: ValidationRule
}
const regs: Regs = {
	// 基础判断
	en: {
		rule: /^[A-Za-z]+$/,
		desc: t('to.validate.desc.en')
	},
	'en-lower': {
		rule: /^[a-z]+$/,
		desc: t('to.validate.desc.enLower')
	},
	'en-upper': {
		rule: /^[A-Z]+$/,
		desc: t('to.validate.desc.enUpper')
	},
	'en-none': {
		rule: /^[^A-Za-z]*$/,
		desc: t('to.validate.desc.enNone')
	},
	cn: {
		rule: /^(?:[\u3400-\u4DB5\u4E00-\u9FEA\uFA0E\uFA0F\uFA11\uFA13\uFA14\uFA1F\uFA21\uFA23\uFA24\uFA27-\uFA29]|[\uD840-\uD868\uD86A-\uD86C\uD86F-\uD872\uD874-\uD879][\uDC00-\uDFFF]|\uD869[\uDC00-\uDED6\uDF00-\uDFFF]|\uD86D[\uDC00-\uDF34\uDF40-\uDFFF]|\uD86E[\uDC00-\uDC1D\uDC20-\uDFFF]|\uD873[\uDC00-\uDEA1\uDEB0-\uDFFF]|\uD87A[\uDC00-\uDFE0])+$/,
		desc: t('to.validate.desc.cn')
	},
	number: {
		rule: /^[-+]?([1-9][0-9]*|0)(\.[0-9]+)?$/,
		desc: t('to.validate.desc.number')
	},
	integer: {
		rule: /^-?[0-9]\d*$/,
		desc: t('to.validate.desc.integer')
	},
	'integer-p': {
		rule: /^\+?[1-9]\d*$/,
		desc: t('to.validate.desc.integerP')
	},
	'integer-n': {
		rule: /^-[1-9]\d*$/,
		desc: t('to.validate.desc.integerN')
	},
	nature: {
		rule: /^\d+$/,
		desc: t('to.validate.desc.nature')
	},
	date: {
		rule: /^\d{4}(-)(1[0-2]|0?\d)\1([0-2]\d|\d|30|31)$/,
		desc: t('to.validate.desc.date')
	},
	time: {
		rule: /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/,
		desc: t('to.validate.desc.time')
	},
	'time-12': {
		rule: /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/,
		desc: t('to.validate.desc.time12')
	},
	// 基本数据格式
	money: {
		rule: /^-?\d+(,\d{3})*(\.\d{1,2})?$/,
		desc: t('to.validate.desc.money')
	},
	ip: {
		rule: /^((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]).){3}(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])(?::(?:[0-9]|[1-9][0-9]{1,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5]))?$/,
		desc: t('to.validate.desc.ip')
	},
	'ip-v6': {
		rule: /^(?:(?:(?:[0-9A-Fa-f]{1,4}:){7}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){6}:[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){5}:([0-9A-Fa-f]{1,4}:)?[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){4}:([0-9A-Fa-f]{1,4}:){0,2}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){3}:([0-9A-Fa-f]{1,4}:){0,3}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){2}:([0-9A-Fa-f]{1,4}:){0,4}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){6}((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|(([0-9A-Fa-f]{1,4}:){0,5}:((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|(::([0-9A-Fa-f]{1,4}:){0,5}((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|([0-9A-Fa-f]{1,4}::([0-9A-Fa-f]{1,4}:){0,5}[0-9A-Fa-f]{1,4})|(::([0-9A-Fa-f]{1,4}:){0,6}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){1,7}:))|\[(?:(?:(?:[0-9A-Fa-f]{1,4}:){7}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){6}:[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){5}:([0-9A-Fa-f]{1,4}:)?[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){4}:([0-9A-Fa-f]{1,4}:){0,2}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){3}:([0-9A-Fa-f]{1,4}:){0,3}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){2}:([0-9A-Fa-f]{1,4}:){0,4}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){6}((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|(([0-9A-Fa-f]{1,4}:){0,5}:((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|(::([0-9A-Fa-f]{1,4}:){0,5}((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|([0-9A-Fa-f]{1,4}::([0-9A-Fa-f]{1,4}:){0,5}[0-9A-Fa-f]{1,4})|(::([0-9A-Fa-f]{1,4}:){0,6}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){1,7}:))\](?::(?:[0-9]|[1-9][0-9]{1,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5]))?$/i,
		desc: t('to.validate.desc.ipV6')
	},
	password: {
		rule: /^\S*(?=\S{6,})(?=\S*\d)(?=\S*[A-Z])(?=\S*[a-z])(?=\S*[!@#$%^&*? ])\S*$/,
		desc: t('to.validate.desc.password'),
		remark: t('to.validate.remark.password')
	},
	username: {
		rule: /^[a-zA-Z0-9_-]{4,16}$/,
		desc: t('to.validate.desc.username'),
		remark: t('to.validate.remark.username')
	},
	// 用户信息
	name: {
		rule: /^([\u4e00-\u9fa5·]{2,16}|[a-zA-Z.\s]{1,20})$/,
		desc: t('to.validate.desc.name')
	},
	'name-cn': {
		rule: /^(?:[\u4e00-\u9fa5·]{2,16})$/,
		desc: t('to.validate.desc.nameCn')
	},
	'name-en': {
		rule: /(^[a-zA-Z]{1}[a-zA-Z\s]{0,20}[a-zA-Z]{1}$)/,
		desc: t('to.validate.desc.nameEn')
	},
	email: {
		rule: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		desc: t('to.validate.desc.email')
	},
	qq: {
		rule: /^[1-9][0-9]{4,10}$/,
		desc: t('to.validate.desc.qq')
	},
	wechat: {
		rule: /^[a-zA-Z][-_a-zA-Z0-9]{5,19}$/,
		desc: t('to.validate.desc.wechat')
	},
	web: {
		rule: /^(((ht|f)tps?):\/\/)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/,
		desc: t('to.validate.desc.web')
	},
	uscc: {
		rule: /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/,
		desc: t('to.validate.desc.uscc')
	},
	bank: {
		rule: /^[1-9]\d{9,29}$/,
		desc: t('to.validate.desc.bank')
	},
	phone: {
		rule: /^1[3456789]\d{9}$/,
		desc: t('to.validate.desc.phone')
	},
	telphone: {
		rule: /^\d{3}-\d{8}$|^\d{4}-\d{7,8}$/,
		desc: t('to.validate.desc.telphone')
	},
	id: {
		rule: /^[1-9]\d{5}(?:18|19|20)\d{2}(?:0[1-9]|10|11|12)(?:0[1-9]|[1-2]\d|30|31)\d{3}[\dXx]$/,
		desc: t('to.validate.desc.id')
	},
	passport: {
		rule: /(^[EeKkGgDdSsPpHh]\d{8}$)|(^(([Ee][a-fA-F])|([DdSsPp][Ee])|([Kk][Jj])|([Mm][Aa])|(1[45]))\d{7}$)/,
		desc: t('to.validate.desc.passport')
	},
	mac: {
		rule: /^((([a-f0-9]{2}:){5})|(([a-f0-9]{2}-){5}))[a-f0-9]{2}$/i,
		desc: t('to.validate.desc.mac')
	}
}

// 判断是否在滚动条以内
function isHideByScroller(node) {
	const el = node
	while (node && !hasClass(node, 'to-scroll') && node.nodeName !== 'BODY') {
		node = (node as VueInstance & { __vueParentComponent?: any; __vue_app__?: any }).__vueParentComponent || node.parentNode
	}
	if (node && node.nodeName !== 'BODY') {
		const scroller = node
		return el.getBoundingClientRect().top > scroller.getBoundingClientRect().top + scroller.offsetHeight || el.getBoundingClientRect().top + el.offsetHeight <= scroller.getBoundingClientRect().top
	} else {
		return false
	}
}

// 显示提示的方法
function showTip(formEl, desc = '') {
	// 元素未被隐藏使用 tooltip
	if (formEl.offsetWidth > 0 && !isHideByScroller(formEl)) {
		const tooltip = createVNode(
			ToTooltip,
			{
				event: '',
				shadow: false,
				position: 'auto',
				align: 'auto',
				color: 'danger',
				width: '',
				lazy: true,
				closeOnBlur: true,
				isScrollClose: true,
				delay: 0,
				mode: ''
			},
			{
				default: () => desc
			}
		)
		const container = document.createElement('div')
		render(tooltip, container)
		document.body.appendChild(container)
		nextTick(() => {
			formEl._tip = {
				vnode: tooltip,
				container
			}
			const open = () => {
				tooltip.component.exposed.open(formEl)
			}
			// const close = () => {
			// 	tooltip.component.exposed.close(formEl)
			// }
			// 参数 click,弹出方式
			// if (binding.arg) {
			// 	formEl.addEventListener('click', open)
			// } else {
			// formEl.addEventListener('mouseenter', open)
			// formEl.addEventListener('mouseleave', close)
			open()
			// }
		})

		// 当前元素获取焦点
		if (formEl.getElementsByTagName('input')[0]) {
			formEl.getElementsByTagName('input')[0].select()
		}
	}

	// 元素被隐藏使用 floattip
	else {
		// ToTip.danger(desc)
		$tip.warning(desc)
	}
}

/**
 * 表单验证函数
 * 该函数用于验证网页表单中的输入是否符合规范
 * 它可以接受一个DOM元素、Vue实例或元素ID作为参数，并验证指定元素及其子元素中带有.to-validate类的元素
 * @param {string|HTMLElement|Vue} dom - 表单的DOM元素、Vue实例或元素ID
 * @param {boolean} isAll - 是否验证所有输入项，默认为false，只验证第一个不符合规则的项
 * @returns {boolean} - 如果所有验证项都通过验证，则返回true；否则返回false
 */
const validate = function (dom: string | HTMLElement | VueInstance, isAll = false): boolean {
	// 存储待验证的DOM节点
	let nodes: Element[] = []

	// 标记验证是否全部通过
	let allPass = true

	// 标记所有在滚动条之外的节点
	const hiddenErrorNodes = []

	// 记录第一个校验不通过的节点（按 DOM 顺序），用于滚动定位
	let firstErrorNode: any = null

	/**
	 * 标记所有在滚动条之外的节点信息
	 * 此函数用于检查给定的表单元素是否在滚动条之外，从而决定是将其标记为可见错误还是隐藏错误
	 * @param {HTMLElement} formEl - 需要检查的表单元素
	 */
	function setHiddenErrorNodes(formEl) {
		// 记录首个错误节点（仅记录第一次）
		if (!firstErrorNode) firstErrorNode = formEl
		// 如果表单元素在滚动条之外，则将其添加到隐藏错误节点列表中
		else {
			hiddenErrorNodes.push(formEl)
		}
	}

	// 根据传入参数类型获取待验证的节点列表
	if (typeof dom === 'string' && document.getElementById(dom)) {
		nodes = Array.from(document.getElementById(dom).querySelectorAll('.to-validate'))
	} else if (dom instanceof HTMLElement) {
		nodes = Array.from(dom.querySelectorAll('.to-validate'))
	} else if (typeof dom !== 'string' && dom && dom.$el) {
		nodes = Array.from(dom.$el.querySelectorAll('.to-validate'))
	}

	// 如果没有找到任何待验证的节点，直接返回true
	if (nodes.length === 0) {
		return true
	}

	// 遍历所有待验证的节点
	for (let i = 0; i < nodes.length; i++) {
		const node = nodes[i]

		// 获取验证规则、是否需要trim空格、描述、是否必填等属性
		const rule = node.getAttribute('rule')
		const trim = node.getAttribute('trim') === 'true'
		const value = (function () {
			if (node.getAttribute('value')) {
				return trim ? node.getAttribute('value').trim() : node.getAttribute('value')
			} else {
				return ''
			}
		})()
		const desc = node.getAttribute('desc')
		const required = node.getAttribute('req') === 'true'
		const formEl = node.parentNode
		// 修改这里，将formEl转换为any类型以访问Vue特定属性
		let formVue = (formEl as any).__vueParentComponent || (formEl as any).__vue_app__?.$parent

		// 特殊处理ToDatePicker组件的验证 (兼容Vue2/Vue3)
		const vueInstance = formVue || (formEl as any).__vue_app__?.$parent
		if (vueInstance && ['ToDatePicker'].includes(vueInstance.$options?.name)) {
			formVue = vueInstance.$refs.inputBox
		}

		// 根据isAll参数决定验证流程
		if (isAll) {
			// 如果值存在且有规则，则应用规则验证
			if (value) {
				if (rule) {
					formVue.exposed.errorType.value = 'rule'
					let reg
					if (regs[rule] && regs[rule].rule) {
						reg = regs[rule].rule
						if (!reg.test(value)) {
							formVue.exposed.errorTip.value = t('to.validate.tip.common', desc, `${regs[rule].desc}${regs[rule].remark ? '，' + regs[rule].remark : ''}`)
							allPass = false
							setHiddenErrorNodes(formEl)
							continue
						} else {
							continue
						}
					} else {
						reg = new RegExp(rule)
						if (!reg.test(value)) {
							formVue.errorTip = t('to.validate.tip.customRemark', desc, formVue.validateTip ? `，${formVue.validateTip}` : '')
							allPass = false
							setHiddenErrorNodes(formEl)
							continue
						} else {
							continue
						}
					}
				} else {
					continue
				}
			} else if (required) {
				formVue.exposed.errorType.value = 'required'
				formVue.exposed.errorTip.value = t('to.validate.tip.requiredRemark', desc)
				allPass = false
				setHiddenErrorNodes(formEl)
				continue
			} else {
				continue
			}
		} else {
			// 如果值存在且有规则，则应用规则验证
			if (value) {
				if (rule) {
					let reg
					if (regs[rule] && regs[rule].rule) {
						reg = regs[rule].rule
						if (!reg.test(value)) {
							showTip(formEl, t('to.validate.tip.common', desc, `${regs[rule].desc}${regs[rule].remark ? '，' + regs[rule].remark : ''}`))
							return false
						} else {
							continue
						}
					} else {
						reg = new RegExp(rule)
						if (!reg.test(value)) {
							showTip(formEl, t('to.validate.tip.customRemark', desc, formVue.validateTip ? `，${formVue.validateTip}` : ''))
							return false
						} else {
							continue
						}
					}
				} else {
					continue
				}
			} else if (required) {
				showTip(formEl, t('to.validate.tip.requiredRemark', desc))
				return false
			} else {
				continue
			}
		}
	}

	// 根据isAll参数返回相应的结果
	if (isAll) {
		// 校验不通过时，自动滚动到第一个不通过的表单项（若其位于 to-scroll 内）
		if (!allPass && firstErrorNode) {
			// 用纯 DOM 向上查找最近的 .to-scroll 容器，兼容 Vue 3
			let scrollEl: any = firstErrorNode
			while (scrollEl && scrollEl.nodeName !== 'BODY' && !(scrollEl.classList && scrollEl.classList.contains('to-scroll'))) {
				scrollEl = scrollEl.parentNode
			}
			if (scrollEl && scrollEl.nodeName !== 'BODY') {
				// 真正可滚动的元素是 .to-scroll-content，回退到 .to-scroll 自身
				const inner = scrollEl.querySelector('.to-scroll-content') || scrollEl
				const targetRect = (firstErrorNode as HTMLElement).getBoundingClientRect()
				const scrollRect = (inner as HTMLElement).getBoundingClientRect()
				;(inner as HTMLElement).scrollTo({
					top: (inner as HTMLElement).scrollTop + targetRect.top - scrollRect.top - 10,
					behavior: 'smooth'
				})
			}
		}
		return allPass
	} else {
		return true
	}
}

const validateAll = function (dom) {
	return validate(dom, true)
}
export { validate, validateAll }
